import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrayHelpers, FloatingMenu, Forms, Loading, Picker, SystemText, DebugInfo } from '@salesperformanceint/e4shared';
import PageHeader from 'components/general/pageHeader';
import NoResults from 'components/general/noResults';
import SortFilter from 'components/general/sortFilter';
import { apiGet, apiPatch } from 'services';
import { setDashboardCollection } from 'actions/dashboard';
import WidgetPicker from './components/widgetPicker';
import RampTimes from './components/widgets/rampTimes';
import CompetencyOverTime from './components/widgets/competencyOverTime';
import CompetencyAgainstMetrics from './components/widgets/competencyMetrics';
import CompetencyGrid from './components/widgets/competencyGrid';
import CompetencyROI from './components/widgets/competencyROI';
import FocusCompetencies from './components/widgets/focusCompetencies';
import GoalsAchieved from './components/widgets/goalsAchieved';
import GoalsAchievedPie from './components/widgets/goalsAchievedPie';
import GoalsOverTime from './components/widgets/goalsOverTime';
import Leaderboard from './components/widgets/leaderboard';
import Matrix from './components/widgets/matrix';
import MetricOverTime from './components/widgets/metricOverTime';
import MetricsOverTime from './components/widgets/metricsOverTime';
import PredictiveModel from './components/widgets/predictiveModel';
import SalesVelocity from './components/widgets/salesVelocity';
import RadarCompetencies from './components/widgets/radarCompetencies';
import TeamRatings from './components/widgets/teamRatings';
import UnknownWidget from './components/widgets/unknownWidget';

import './myDashboard.css';

import RGL, { WidthProvider } from 'react-grid-layout';
import ManageDashboards from './components/manageDashboards';
import BlobImage from 'components/blobImage';
import { setPrismMenu, setShowSuccess, toggleDashboardLock } from 'actions/ui';
import { widgets as availableWidgets } from 'data/widgets';
import { hasModule } from 'helpers/profile';
import queryString from 'query-string';
import { translateCompetency } from 'helpers/translations';

import posthog from 'posthog-js';
import ActionPlanner from './components/widgets/actionPlanner';

const ReactGridLayout = WidthProvider(RGL);

export const MyDashboard = ({ history, location }) => {
    const dispatch = useDispatch();
    const queryValues = queryString.parse(location.search);

    const defaultProps = {
        className: 'layout',
        items: 20,
        rowHeight: 100,
        cols: 6,
        height: item => 110 * item.h - 100,
    };

    const container = React.useRef(null);
    const user = useSelector(state => state.user.user);
    const isPrism = user.companySettings?.prism_UI;
    const min = 1000;

    if (!user.administrator && user.staff === 0) history.replace('/');

    const leftAsideOpen = useSelector(state => state.ui.leftAsideOpen);
    const showFilters = useSelector(state => state.ui.filterWidget);
    const locked = useSelector(state => state.ui.dashboardLocked);
    const hasCRM = hasModule(user, 'integrations') && user.crmIntegration;

    const [showAddNew, setShowAddNew] = React.useState(false);
    const [newY, setNewY] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [showAssign, setShowAssign] = React.useState(false);
    const [showManageDashboards, setShowManageDashboards] = React.useState(false);
    const [dashboardId, setDashboardId] = React.useState(parseInt(queryValues.id, 10) || 0);
    const [divWidth, setDivWidth] = React.useState(0);
    const [dashboard, setDashboard] = React.useState({});
    const [dashboards, setDashboards] = React.useState([]);
    const [availableAssignees, setAvailableAssignees] = React.useState([]);
    const [menuPositionSet, setMenuPositionSet] = React.useState(false);
    const [defaultChecked, setDefaultChecked] = React.useState(false);
    const [loadingFilters, setLoadingFilters] = React.useState(true);
    const { businessUnits, departments, locations, roles, teams } = useSelector(state => state.dashboard);

    const [availableAssigneesBasic, setAvailableAssigneesBasic] = React.useState([]);

    const checkDefault = e => {
        setDefaultChecked(e.target.checked);
    };

    const floatingMenu = React.useRef(null);

    const getAccelerateRedirectPath = () => {
        if (user.administrator) return 'a/dashboard';
        if (user.isManager) return 't/my-team';

        return 'today';
    };

    const api = {
        accelerate: () => {
            posthog.capture('click_accelerate_learn_insights_button', null, { transport: 'sendBeacon' });
            apiGet(
                `accelerate-passthrough?redirect=${getAccelerateRedirectPath()}`,
                user.guid,
                response => {
                    window.location.href = response;
                },
                null,
                true
            );
        },
        loadBoards: () => {
            setLoading(true);
            setMenuPositionSet(false);
            apiGet('dashboards', user.guid, response => {
                setDashboards(response);
                let initialBoard = response.find(d => d.id === dashboardId);
                if (!initialBoard) initialBoard = response.find(d => d.default);
                setDashboard({ ...initialBoard });
                setDashboardId(initialBoard.id);
                setLoading(false);
            });
        },
        saveLayout: widgets => {
            apiPatch(`widget-layout/${dashboardId}`, widgets, user.guid);
        },
        assign: (assigned_Ids, checked) => {
            apiPatch(`dashboard-assign/${dashboardId}`, { assigned_Ids, default: checked }, user.guid, () => {
                dispatch(setShowSuccess(SystemText('Dashboard/Assign/Success')));
                setShowAssign(false);
                api.loadBoards(true);
            });
        },
    };

    const resizeHandler = () => {
        setTimeout(() => {
            container.current && setDivWidth(container.current.offsetWidth);
        }, 100);
    };

    const setLocked = x => {
        dispatch(toggleDashboardLock());
    };

    const filterArray = (data, ids) => {
        return ids?.length
            ? ids.reduce((acc, id) => {
                  const item = data?.find(l => l.id === id);
                  if (item?.name) acc.push(item.name);
                  return acc;
              }, [])
            : [];
    };

    React.useEffect(() => {
        dispatch(setPrismMenu('Insights'));
    }, []);

    React.useEffect(() => {
        DebugInfo('DashboardId', dashboardId?.toString() || '');
    }, [dashboardId]);

    React.useEffect(() => {
        api.loadBoards();

        apiGet(`profile/${user.id}/downline`, user.guid, response =>
            dispatch(
                setDashboardCollection(
                    'profiles',
                    response.map(p => ({ ...p, name: p.full_Name }))
                )
            )
        );

        apiGet('all-quotas', user.guid, response =>
            dispatch(
                setDashboardCollection(
                    'goals',
                    response.filter(x => x.metric_Id > 0).sort((a, b) => (a.sequence > b.sequence ? 1 : -1))
                )
            )
        );

        apiGet('competency', user.guid, response =>
            dispatch(
                setDashboardCollection(
                    'competencies',
                    response.filter(c => c.active).map(c => translateCompetency(user.language_Code, c))
                )
            )
        );

        apiGet('all-fields', user.guid, response => dispatch(setDashboardCollection('metrics', response)));

        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, []);

    React.useEffect(() => {
        const promises = [];

        if (!roles?.length) {
            promises.push(apiGet('role', user.guid, response => dispatch(setDashboardCollection('roles', response))));
        }

        if (!departments?.length) {
            promises.push(
                apiGet('department', user.guid, response => dispatch(setDashboardCollection('departments', response)))
            );
        }

        if (!locations?.length) {
            promises.push(
                apiGet('location', user.guid, response => {
                    dispatch(setDashboardCollection('locations', response));
                })
            );
        }

        if (!businessUnits?.length) {
            promises.push(
                apiGet('business-unit', user.guid, response => dispatch(setDashboardCollection('units', response)))
            );
        }

        if (!teams?.length) {
            promises.push(
                apiGet('dashboard-teams', user.guid, response =>
                    dispatch(
                        setDashboardCollection(
                            'teams',
                            response.map(u => ({ ...u, name: u.full_Name }))
                        )
                    )
                )
            );
        }

        if (user.administrator) {
            promises.push(
                apiGet('dashboard-assignees', user.guid, response => {
                    setAvailableAssigneesBasic(response.map(u => ({ ...u, name: u.full_Name })));
                })
            );
        }

        if (!promises.length) {
            setLoadingFilters(false);
        }

        Promise.all(promises).then(() => {
            setLoadingFilters(false);
        });
    }, []);

    React.useEffect(() => {
        if (loadingFilters) {
            return;
        }

        if (user.administrator) {
            setAvailableAssignees(
                availableAssigneesBasic.map(u => ({
                    ...u,
                    location_Names: filterArray(locations, u.location_Ids),
                    department_Names: filterArray(departments, u.department_Ids),
                    business_Unit_Names: filterArray(businessUnits, u.business_Unit_Ids),
                    role_Names: filterArray(roles, u.role_Ids),
                }))
            );
        }
    }, [loadingFilters]);

    React.useEffect(() => {
        setTimeout(() => window.dispatchEvent(new Event('resize')), 250);
    }, [leftAsideOpen]);

    React.useEffect(() => {
        !loading && resizeHandler();
    }, [loading]);

    React.useEffect(() => {
        if (dashboard?.widgets) {
            setNewY(dashboard.widgets.reduce((all, c) => (c.y + c.h > all ? c.y + c.h : all), 0));
        }
    }, [dashboard]);

    React.useEffect(() => {
        setDashboard({ ...dashboards.find(d => d.id === dashboardId || (dashboardId === 0 && d.default)) });
        const menu = document.getElementsByClassName('floating-menu')[0];
        if (menu) {
            const list = menu.getElementsByTagName('ul')[0];
            list.style.left = null;
            list.style.width = null;
            setMenuPositionSet(false);
        }
    }, [dashboardId]);

    const extraButtons = () => (
        <div>
            {user.administrator && dashboard && !dashboard.profile_Id && (
                <button
                    data-testid="mydashboard-button-assign"
                    className="btn btn-success"
                    onClick={() => {
                        setShowAssign(true);
                    }}
                >
                    <i className="fa fa-users mr10" />
                    {SystemText('Dashboard/Buttons/Assign')}
                </button>
            )}
            <button
                data-testid="mydashboard-button-add"
                className="btn btn-success"
                disabled={!dashboard.profile_Id && !user.administrator}
                onClick={() => {
                    setShowAddNew(true);
                }}
            >
                <i className="fa fa-plus mr10" />
                {SystemText('Dashboard/Buttons/New')}
            </button>
            {!isPrism && user.subDomain && (
                <button data-testid="mydashboard-button-accelerate" className="btn btn-success" onClick={api.accelerate}>
                    {SystemText('Dashboard/Buttons/Accelerate')}
                    <i className="fa fa-external-link ml10" />
                </button>
            )}
        </div>
    );

    const realChange = props => {
        let result = false;
        dashboard.widgets.forEach((t, i) => {
            if (t.x !== props[i].x) result = true;
            if (t.y !== props[i].y) result = true;
            if (t.h !== props[i].h) result = true;
            if (t.w !== props[i].w) result = true;
        });

        return result;
    };

    const onLayoutChange = ({ ...props }) => {
        if (realChange(props) && divWidth >= min) {
            const temp = { ...dashboard };
            temp.widgets.forEach((t, i) => {
                t.x = props[i].x;
                t.y = props[i].y;
                t.h = props[i].h;
                t.w = props[i].w;
            });

            api.saveLayout(temp.widgets);
            setDashboard({ ...temp });
        }
    };

    const checkHeight = () => {
        setTimeout(() => {
            if (!menuPositionSet) {
                const menu = document.getElementsByClassName('floating-menu')[0];
                const list = menu.getElementsByTagName('ul')[0];
                const height = list.clientHeight;
                container.current.style.minHeight = `${height + 70}px`;

                const dash = document.getElementById('my-dashboard');
                const dash_x = dash.getBoundingClientRect().x;
                const menu_x = menu.getBoundingClientRect().x;
                const menuWidth = menu.getBoundingClientRect().width;
                const listWidth = list.getBoundingClientRect().width;

                const menuRight = menu_x - dash_x + menuWidth;
                const tooBig = listWidth > menuRight;

                if (tooBig) {
                    list.style.left = `-${menu_x - dash_x + 10}px`;
                    let widest = Array.from(list.getElementsByTagName('li')).reduce(
                        (a, c) => (a > c.scrollWidth ? a : c.scrollWidth),
                        0
                    );

                    list.style.width = `${widest + 10}px`;
                }

                setMenuPositionSet(true);
            }
        }, 10);
    };

    const selectOthers = () => {
        if (!dashboards || !dashboard) return null;

        const showCompanyBoards = user.administrator && dashboards.find(d => !d.profile_Id);

        const item = (d, i) => (
            <li key={`dash-${d.id}-${i}`} onClick={() => setDashboardId(d.id)}>
                <i className="fa fa-dashboard mr10" />
                {d.name}
            </li>
        );

        return (
            <div className="flex flex-center">
                <div className="title-floatingmenu" ref={floatingMenu}>
                    <FloatingMenu buttonText={dashboard.name} onShow={checkHeight}>
                        {showCompanyBoards && <li className="sub-header">{SystemText('Dashboard/Dropdown/YourBoards')}</li>}
                        {dashboards
                            .filter(d => d.profile_Id || d.assigned_Ids?.some(x => x === user.id))
                            .map((d, i) => item(d, i))}
                        {showCompanyBoards && (
                            <>
                                <li className="sub-header">{SystemText('Dashboard/Dropdown/CompanyBoards')}</li>
                                {dashboards.filter(d => !d.profile_Id).map((d, i) => item(d, i))}
                            </>
                        )}
                        <li className="add-new text-center">
                            <button
                                data-testid="mydashboard-button-manage"
                                className="no-wrap btn btn-link btn-block text-center"
                                onClick={() => setShowManageDashboards(true)}
                            >
                                {SystemText('Dashboard/Buttons/Manage')}
                            </button>
                        </li>
                    </FloatingMenu>
                </div>
                {locked ? (
                    <button
                        className="btn ml10 btn-xs btn-danger hidden-sm"
                        onClick={() => setLocked(false)}
                        title={SystemText('Dashboard/Buttons/Unlock')}
                    >
                        <i className="fa fa-lock mr10" />
                        {SystemText('Dashboard/Locked')}
                    </button>
                ) : (
                    <button
                        className="btn btn-power ml10 btn-xs btn-success hidden-sm"
                        onClick={() => setLocked(true)}
                        title={SystemText('Dashboard/Buttons/Lock')}
                    >
                        <i className="fa fa-unlock mr10" />
                        {SystemText('Dashboard/Unlocked')}
                    </button>
                )}
            </div>
        );
    };

    const dropItem = id => {
        const temp = dashboard;
        temp.widgets = temp.widgets.filter(w => w.id !== id);
        setDashboard(temp);
    };

    const setItem = item => {
        const temp = [...dashboards];
        const board = temp.find(d => d.id === item.dashboard_Id);
        const widget = board.widgets.find(w => w.id === item.id);
        for (let k of Object.keys(item)) widget[k] = item[k];
        setDashboards(temp);
    };

    const mappedWidget = item => {
        const defaults = {
            defaultProps: { ...defaultProps, company: !dashboard.profile_Id },
            item,
            setItem,
            id: item.id,
            onDelete: id => {
                api.loadBoards(true);
                dropItem(id);
            },
        };

        const availableWidget = availableWidgets.find(w => w.widget === item.widget);

        if (!hasCRM && availableWidget.crmOnly) return null;

        switch (item.widget) {
            case 'actionPlanner':
                return <ActionPlanner {...defaults} />;
            case 'competencyOverTime':
                return <CompetencyOverTime {...defaults} />;
            case 'competencyAgainstMetric':
                return <CompetencyAgainstMetrics {...defaults} />;
            case 'competencyGrid':
                return <CompetencyGrid {...defaults} />;
            case 'competencyROI':
                return <CompetencyROI {...defaults} />;
            case 'focusCompetencies':
                return <FocusCompetencies {...defaults} />;
            case 'goalsAchieved':
                return <GoalsAchieved {...defaults} />;
            case 'goalsAchievedPie':
                return <GoalsAchievedPie {...defaults} />;
            case 'goalsOverTime':
                return <GoalsOverTime {...defaults} />;
            case 'leaderboard':
                return <Leaderboard {...defaults} />;
            case 'matrix':
                return <Matrix {...defaults} />;
            case 'metricOverTime':
                return <MetricOverTime {...defaults} />;
            case 'metricsOverTime':
                return <MetricsOverTime {...defaults} />;
            case 'predictiveModel':
                return <PredictiveModel {...defaults} />;
            case 'rampTimes':
                return <RampTimes {...defaults} />;
            case 'salesVelocity':
                return <SalesVelocity {...defaults} />;
            case 'radarCompetencies':
                return <RadarCompetencies {...defaults} />;
            case 'teamRatings':
                return <TeamRatings {...defaults} />;
            default:
                return <UnknownWidget {...defaults} />;
        }
    };

    const widgetsMapped = () => {
        let all_y = 0;
        return dashboard.widgets.map((item, i) => {
            const w = divWidth < min ? 6 : item.w;
            const x = divWidth < min ? 0 : item.x;
            const y = divWidth < min ? all_y : item.y;
            const h = item.h;
            all_y += item.h;

            return (
                <div className="grid-item" key={`${item.id}-${i}`} data-grid={{ x, y, w, h, minH: 2 }}>
                    <span>{mappedWidget(item)}</span>
                </div>
            );
        });
    };

    return (
        <div id="my-dashboard" ref={container}>
            <PageHeader title={SystemText('Dashboard/Title')} extra={extraButtons} after={loading ? null : selectOthers} />
            <section className="content mtm10" data-testid="mydashboard-content">
                <SortFilter toggle={null} filterFields={null} />
                {loading ? (
                    <Loading />
                ) : !dashboard?.widgets?.length ? (
                    <div className="mt50">
                        <NoResults
                            title={SystemText('Dashboard/NoResults/Title')}
                            message={SystemText('Dashboard/NoResults/Message')}
                        />
                    </div>
                ) : (
                    <ReactGridLayout
                        key={`reload-${divWidth}`}
                        {...defaultProps}
                        isDraggable={Boolean(
                            !locked &&
                            showFilters === null &&
                            divWidth >= min &&
                            (user.administrator || dashboard.profile_Id)
                        )}
                        draggableCancel="a,button,.pointer,input"
                        isResizable={Boolean(
                            !locked &&
                            showFilters === null &&
                            divWidth >= min &&
                            (user.administrator || dashboard.profile_Id)
                        )}
                        onBreakpointChange={null}
                        onLayoutChange={onLayoutChange}
                        onDrop={null}
                        measureBeforeMount={false}
                        useCSSTransforms={false}
                        compactType="vertical"
                        preventCollision={false}
                    >
                        {widgetsMapped()}
                    </ReactGridLayout>
                )}
            </section>
            {dashboard && (
                <WidgetPicker
                    show={showAddNew}
                    refresh={() => api.loadBoards(true)}
                    close={() => setShowAddNew(false)}
                    dashboardId={dashboard.id}
                    newY={newY}
                />
            )}
            <ManageDashboards
                show={showManageDashboards}
                dashboards={dashboards}
                refresh={() => api.loadBoards(true)}
                close={() => setShowManageDashboards(false)}
            />
            {ArrayHelpers.existsAndHasLength(availableAssignees) && (
                <Picker
                    title={SystemText('Dashboard/Assign/Title', dashboard?.name)}
                    entity="Users"
                    data={availableAssignees}
                    value={dashboard?.assigned_Ids}
                    filterOption="staff"
                    footerExtra={() => (
                        <label>
                            <i className={`fa fa-${defaultChecked ? 'square-check' : 'square'}`} />
                            <input onChange={e => checkDefault(e)} type="checkbox" style={{ display: 'none' }} />
                            <span className="ml10">{SystemText('Dashboard/Assign/Default')}</span>
                        </label>
                    )}
                    show={showAssign}
                    onSave={ids => {
                        api.assign(ids, defaultChecked);
                    }}
                    onClose={() => {
                        setShowAssign(false);
                    }}
                    itemPrefix={p => (
                        <div className="image-holder mr10">
                            <BlobImage folder="avatar" initials={p.initials} src={p.avatar} />
                        </div>
                    )}
                />
            )}
        </div>
    );
};

export default MyDashboard;
